import {
  Avatar,
  CircularProgress
} from '@mui/material';
import {
  debounce
} from 'lodash';
import React from 'react';
import LogoGreenhouse from '../../../assets/images/logo__gh.png';
import {
  Arr
} from '../../../lib/Array.lib';
import {
  NOT,
  YES
} from '../../../lib/Boolean.lib';
import Core from '../../../lib/Core';
import {
  ID__ATS_TYPE__GH_HARVEST
} from '../../../lib/Definition';
import Employer from '../../../lib/Employer';
import {
  Fun
} from '../../../lib/Function.lib';
import useState from '../../../lib/hooks/useState.hook';
import {
  Obj
} from '../../../lib/Object.lib';
import getAtsContextProperty from '../../../lib/services/ATS/getAtsContextProperty.fun';
import getAtsContextUpdated from '../../../lib/services/ATS/getAtsContextUpdated.fun';
import GreenhouseHarvest from '../../../lib/services/ATS/Greenhouse.harvest.lib';
import {
  fetchGreenhouseReferrers
} from '../../../lib/services/ATS/Greenhouse/fetchGreenhouseReferrers.async';
import {
  fetchGreenhouseSources
} from '../../../lib/services/ATS/Greenhouse/fetchGreenhouseSources.async';
import {
  COLLECTION__EMPLOYERS,
  updateLoopbackRecord
} from '../../../lib/services/BE/loopback.api';
import {
  trim
} from '../../../lib/String.lib';
import {
  matchLocation
} from '../../../lib/URL.lib';
import {
  showConfirm
} from '../../Dialogs/AppConfirmationDialog';
import {
  THEME__COLOR__ERROR,
  THEME__COLOR__SUCCESS
} from '../../Layout/Libraries/Theme.lib';
import Box from '../../Layout/Wrappers/Box';
import Button from '../../Layout/Wrappers/Button';
import Chip from '../../Layout/Wrappers/Chip';
import Fieldset from '../../Layout/Wrappers/Fieldset';
import IconButton from '../../Layout/Wrappers/IconButton';
import Menu from '../../Layout/Wrappers/Menu';
import Once from '../../Layout/Wrappers/Once';
import TextField from '../../Layout/Wrappers/TextField';
import Typography from '../../Layout/Wrappers/Typography';
import {
  getControllerButtonOpenEmployerSettings
} from './ButtonOpenEmployerSettings';

export default function SubSectionAtsSettingsGhHarvest({
  acl = true,
  entity = {},
  onChange = Fun,
  ...props
}) {

  const [state, updateState] = useState();
  const mem = React.useRef({});
  React.useEffect(() => {
    mem.current.entity = entity;
  }, [entity]);
  if (NOT(acl)) { return null; }

  const atsTypeId = ID__ATS_TYPE__GH_HARVEST;

  // apiKey
  const VALUE__ATS__API_KEY = getAtsContextProperty({
    entity,
    atsTypeId,
    property: 'apiKey'
  });

  // referrerId
  const VALUE__ATS__REFERRER__ID = +getAtsContextProperty({
    entity,
    atsTypeId,
    property: 'referrerId'
  });

  const connectClient = async () => {
    await updateState({
      busyConnect: true
    });
    try {
      await updateState({
        isConnected: YES(
          Obj(
            await GreenhouseHarvest.connect({
              employerId: entity.id,
            })
          ).connected
        )
      });
      await fetchAtsSources();
      await fetchAtsReferrers();
    }
    catch (error) {
      console.error(error);
      Core.showError('Failed to connect to Greenhouse Harvest');
    }
    await updateState({
      busyConnect: false
    });
  }

  const disconnectClient = async () => {
    await updateState({
      busyConnect: true
    });
    try {
      await updateState({
        isConnected: YES(
          Obj(
            await GreenhouseHarvest.disconnectClient({
              employerId: entity.id,
            })
          ).connected
        ),
        referrers: [],
        sources: []
      });
      onChange(
        getAtsContextUpdated({
          entity: mem.current.entity,
          atsTypeId,
          update: {
            clientId: '',
            clientSecret: '',
            referrerId: '',
            sourceId: '',
            userId: '',
          }
        })
      );
    }
    catch (error) {
      Core.showError(error);
    }
    await updateState({
      busyConnect: false
    });
  }

  const onClickEditApiKeys = () => {
    function FieldsetClientId(props) {
      const [state, updateState] = useState();
      mem.current.FieldsetClientId = { state, updateState };
      return (
        <Fieldset
          title='Client ID'
          subtitle='Username for authentication credential to ATS'
        >
          <TextField disabledBlurExport
            value={state.clientId}
            onChange={(event) => updateState({ clientId: event.target.value })}
          />
        </Fieldset>
      );
    }
    function FieldsetClientSecret(props) {
      const [state, updateState] = useState();
      mem.current.FieldsetClientSecret = { state, updateState };
      return (
        <Fieldset
          title='Client Secret'
          subtitle='Password for authentication credential to ATS.'
        >
          <TextField disabledBlurExport
            value={state.clientSecret}
            onChange={(event) => updateState({ clientSecret: event.target.value })}
          />
        </Fieldset>
      );
    }
    showConfirm({
      title: 'Greenhouse integration',
      content: (
        <Box column>
          <FieldsetClientId />
          <FieldsetClientSecret />
        </Box>
      ),
      onAcceptLabel: 'Connect',
      onAccept: async (event) => {
        try {
          const clientId = trim(mem.current.FieldsetClientId?.state?.clientId);
          const clientSecret = trim(mem.current.FieldsetClientSecret?.state?.clientSecret);
          if (NOT(clientId) || NOT(clientSecret)) {
            throw new Error('Client ID and Secret are required');
          }
          const update = getAtsContextUpdated({
            entity: mem.current.entity,
            atsTypeId,
            update: { clientId, clientSecret }
          });
          console.debug('Update:', update, { clientId, clientSecret });
          Employer.cleanCache();
          await updateLoopbackRecord({
            collection: COLLECTION__EMPLOYERS,
            id: entity.id,
            record: update
          });
          await disconnectClient();
          onChange(update);
          debounce(connectClient, 500)();
        }
        catch (error) {
          Core.showError(error);
        }
      },
      paperStyle: {
        width: sizes.m7
      }
    });
  }

  const onClickDisconnect = () => {
    showConfirm({
      title: 'Greenhouse integration',
      message: (
        <Box column>
          <Typography className='c-black-dark f-md fw-400'>
            Disconnecting 10 By 10's active API integration will delay candidate submissions and event processing
          </Typography>
          <br />
          <Typography className='c-black-dark f-md fw-400'>
            Are you sure you want to proceed?
          </Typography>
        </Box>
      ),
      onAcceptLabel: 'Disconnect',
      onAccept: async () => {
        const update = getAtsContextUpdated({
          entity: mem.current.entity,
          atsTypeId,
          update: {
            clientId: '',
            clientSecret: ''
          }
        });
        console.debug('Update:', update);
        Employer.cleanCache();
        await updateLoopbackRecord({
          collection: COLLECTION__EMPLOYERS,
          id: entity.id,
          record: update
        });
        onChange(update);
        debounce(disconnectClient, 500)();
      }
    });
  }

  const YES__CONNECTED = YES(state.isConnected);
  const NOT__CONNECTED = NOT(YES__CONNECTED);

  const LOADER = <CircularProgress className='icon16 mr-1' />;

  // sourceId
  const VALUE__ATS__SOURCE_ID = +getAtsContextProperty({
    entity,
    atsTypeId,
    property: 'sourceId'
  });

  // YES sources
  const YES__ATS__SOURCES = YES(
    Arr(state.sources).length
  );

  const fetchAtsSources = async (event) => {
    await updateState({
      busySources: true
    });
    const sources = Arr(
      await fetchGreenhouseSources({
        employerId: entity.id
      })
    )
      .filter(
        (source) => /10(x|by)10/i.test(source.name)
      )
      .map(
        (source) => ({
          ...source,
          id: source.id,
          label: `${source.name} (${source.type?.name})`,
        })
      );
    await updateState({
      busySources: false,
      sources: sources
    });
    const sourceId = Arr(sources).find((source) => /10(x|by)10/i.test(source.name))?.id;
    const currentSourceId = getAtsContextProperty({
      entity: mem.current.entity,
      atsTypeId,
      property: 'sourceId'
    });
    if (NOT(currentSourceId)) {
      onChange(
        getAtsContextUpdated({
          entity: mem.current.entity,
          atsTypeId,
          update: {
            sourceId
          }
        })
      );
    }
  }

  const fetchAtsReferrers = async () => {
    await updateState({
      busyReferrer: true
    });
    const referrers = Arr(
      await fetchGreenhouseReferrers({
        employerId: entity.id,
      })
    )
      .filter(
        (referrer) => /10(x|by)10/i.test(referrer.name)
      )
      .map(
        (referrer) => ({
          ...referrer,
          id: referrer.id,
          label: referrer.name,
        })
      );
    const referrer = Obj(referrers.find(
      (referrer) => /10(x|by)10/i.test(referrer.name) && /(team|recruiter)/i.test(referrer.name)
    ));
    await updateState({
      referrers,
      busyReferrer: false
    });
    const currentReferrerId = getAtsContextProperty({
      entity: mem.current.entity,
      atsTypeId,
      property: 'referrerId'
    });
    if (NOT(currentReferrerId)) {
      onChange(
        getAtsContextUpdated({
          entity: mem.current.entity,
          atsTypeId,
          update: {
            referrerId: referrer.id,
            userId: referrer.user_id,
          }
        })
      );
    }
  }

  return (
    <Box column flex1 {...props}>

      <Box row
        className='p-1'
      >
        <Box
          className='p-05 outlined rounded mr-1 bg-white'
        >
          <Avatar
            src={LogoGreenhouse}
            variant='rounded'
          />
        </Box>
        <Box column flex1>
          <Box row flex1>
            <Typography className='fw-500 f-md mr-1'>
              Greenhouse
            </Typography>
            <Chip slim
              acl={YES__CONNECTED}
              color={THEME__COLOR__SUCCESS}
            >
              Connected
            </Chip>
            <Chip slim
              acl={NOT__CONNECTED}
              color={THEME__COLOR__ERROR}
            >
              Disconnected
            </Chip>
            {state.busyConnect && LOADER}
            <Once
              acl={VALUE__ATS__API_KEY}
              promise={connectClient}
              render={false}
            />
          </Box>
          <Typography className='fw-400 f-sm'>
            Applicant Tracking System
          </Typography>
        </Box>
        <Box className='flex-align-right-top'
          acl={matchLocation(/employer\/pendings/i)}
        >
          <IconButton
            icon='close'
            onClick={(event) => {
              getControllerButtonOpenEmployerSettings().updateState({
                openMainDialog: false
              });
            }}
            style={{
              marginTop: -30,
              marginRight: -10,
            }}
          />
        </Box>
      </Box>

      <Box row
        acl={YES__CONNECTED}
        className='flex-align-left-top'
      >
        <Fieldset
          acl={Core.isAdmin()}
          title='Source'
          subtitle='Select a source from the list'
          nextToTitle={state.busySources && LOADER}
        >
          <Once
            acl={
              VALUE__ATS__API_KEY &&
              !YES__ATS__SOURCES
            }
            promise={fetchAtsSources}
            render={false}
          />
          <Menu dropdown
            value={VALUE__ATS__SOURCE_ID}
            onChange={(value) => {
              onChange(
                getAtsContextUpdated({
                  entity,
                  atsTypeId,
                  update: {
                    sourceId: value
                  }
                })
              );
            }}
            options={Arr(
              state.sources
            )}
            onClick={fetchAtsSources}
          />
        </Fieldset>
        <Fieldset
          title='Referrer'
          nextToTitle={state.busyReferrer && LOADER}
          subtitle='Select a referrer from the list'
        >
          <Once
            acl={VALUE__ATS__API_KEY}
            promise={fetchAtsReferrers}
            render={false}
          />
          <Menu dropdown
            value={VALUE__ATS__REFERRER__ID}
            onChange={(value) => {
              onChange(
                getAtsContextUpdated({
                  entity,
                  atsTypeId,
                  update: {
                    referrerId: value,
                    userId: Obj(
                      state.referrers.find(
                        (referrer) => referrer.id === value
                      )
                    ).user_id
                  }
                })
              );
            }}
            options={Arr(
              state.referrers
            )}
            onClick={fetchAtsReferrers}
          />
        </Fieldset>
      </Box>

      <Box row
        className='p-1 align-right'
      >
        <Button outlined
          acl={YES__CONNECTED}
          label='Disconnect API'
          onClick={onClickDisconnect}
          className='min-w-160 mr-1'
        />
        <Button primary
          label='Edit API keys'
          onClick={onClickEditApiKeys}
          className='min-w-160'
        />
      </Box>

    </Box>
  );
}
